/**
 * 🎮 GAMES TAB CONTAINER - Architecture Mozart/Jobs/Tesla
 * 
 * Conteneur pour l'onglet Jeux dans Heartbeats.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Gamepad2, Brain, Sparkles } from 'lucide-react';
import { Button } from '../../ui/button';
import { useGames } from '../../../hooks/heartbeats';
import { WouldYouRather, CompatibilityQuiz, GameLobby } from '../features/games';

export const GamesTab = ({ customerId, isDarkMode }) => {
  const games = useGames(customerId);
  const [showWouldYouRather, setShowWouldYouRather] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);

  const gameOptions = [
    {
      id: 'would-you-rather',
      title: 'Tu préfères ?',
      description: 'Choix absurdes et dilemmes fous',
      emoji: '🎮',
      icon: Gamepad2,
      color: 'from-purple-500 to-pink-500',
      onClick: () => {
        games.startWouldYouRather();
        setShowWouldYouRather(true);
      }
    },
    {
      id: 'compatibility-quiz',
      title: 'Quiz Compatibilité',
      description: 'Découvre ta personnalité dating',
      emoji: '🧩',
      icon: Brain,
      color: 'from-blue-500 to-cyan-500',
      onClick: () => {
        games.startCompatibilityQuiz();
        setShowQuiz(true);
      }
    },
    {
      id: 'truth-or-dare',
      title: 'Vérité ou Défi',
      description: 'Le classique indemodable',
      emoji: '🎲',
      icon: Sparkles,
      color: 'from-orange-500 to-red-500',
      disabled: true,
      comingSoon: true
    }
  ];

  return (
    <div className={`p-4 space-y-6 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          🎮 Mini-Jeux
        </h2>
        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Amuse-toi et découvre les autres différemment !
        </p>
      </div>

      {/* Game Cards */}
      <div className="grid gap-4">
        {gameOptions.map((game, index) => (
          <motion.div
            key={game.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <button
              onClick={game.onClick}
              disabled={game.disabled}
              className={`w-full p-4 rounded-2xl text-left transition-all ${game.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-[1.02] active:scale-[0.98]'} ${
                isDarkMode 
                  ? 'bg-gray-800/50 border border-gray-700 hover:border-purple-500/50' 
                  : 'bg-white border border-gray-200 hover:border-purple-300 shadow-sm'
              }`}
            >
              <div className="flex items-center gap-4">
                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${game.color} flex items-center justify-center text-white text-2xl shadow-lg`}>
                  {game.emoji}
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {game.title}
                    </h3>
                    {game.comingSoon && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">
                        Bientôt
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {game.description}
                  </p>
                </div>

                {/* Arrow */}
                {!game.disabled && (
                  <div className={`text-2xl ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    ›
                  </div>
                )}
              </div>
            </button>
          </motion.div>
        ))}
      </div>

      {/* Would You Rather Modal */}
      {showWouldYouRather && (
        <WouldYouRather
          customerId={customerId}
          isDarkMode={isDarkMode}
          questions={games.questions}
          onClose={() => {
            setShowWouldYouRather(false);
            games.resetGame();
          }}
        />
      )}

      {/* Quiz Modal */}
      {showQuiz && (
        <CompatibilityQuiz
          customerId={customerId}
          isDarkMode={isDarkMode}
          questions={games.questions}
          onClose={() => {
            setShowQuiz(false);
            games.resetGame();
          }}
        />
      )}
    </div>
  );
};

export default GamesTab;
